import { useState, useRef } from "react";
import { CustomTypography } from "../../../../components/CustomTypography/CustomTypography";
import { Icon } from "../../../../components/Icon/Icon";
import { IconWithBackground } from "../../../../components/IconWithBackground/IconWithBackground";
import { colors } from "../../../../styles/colors";
import { dataDocuments } from "./data";
import useStyles from "./styles";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { useParams } from "react-router";
import {
  useGetDocumentByVehicleId,
  useCreateDocument,
} from "../../../../api/document";
import { uploadToStorage } from "../../../../api/media";
import { openDocument } from "../../../../hooks/useDocumentViewer";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

type Props = {};

export const Documents: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { id = "" } = useParams();
  const vehicleId = parseInt(id);

  const { documents } = useGetDocumentByVehicleId(vehicleId);

  const [uploadingType, setUploadingType] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingDocType, setPendingDocType] = useState<string | null>(null);

  const { mutate: createDocument } = useCreateDocument(() => {
    queryClient.invalidateQueries({
      queryKey: ["document", "id", vehicleId],
    } as any);
    setUploadingType(null);
  });

  const getDocumentTypeKey = (docId: string): string => {
    switch (docId) {
      case "1":
        return "contract";
      case "2":
        return "registration_certificate";
      case "3":
        return "insurance_certificate";
      default:
        return "";
    }
  };

  const getDocumentForType = (docId: string) => {
    const typeKey = getDocumentTypeKey(docId);
    return documents?.find((doc: any) => {
      // API may return type as a string or as document_type.name
      const docType = doc.type || doc.document_type?.name || doc.name;
      return docType === typeKey;
    });
  };

  const hasDocument = (docId: string): boolean => {
    const doc = getDocumentForType(docId) as any;
    return !!(doc?.scan_id || doc?.scan);
  };

  const openDocumentOrWarn = async (docId: string) => {
    const doc = getDocumentForType(docId) as any;
    const scanValue = doc?.scan_id || doc?.scan;
    if (scanValue) {
      await openDocument(scanValue);
    } else {
      toast.error(
        t(
          "panel.document.notAvailable",
          "This document has not been uploaded yet"
        )
      );
    }
  };

  const handleUploadClick = (docId: string) => {
    setPendingDocType(docId);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !pendingDocType) return;

    const typeKey = getDocumentTypeKey(pendingDocType);
    if (!typeKey) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/webp",
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error(
        t(
          "panel.document.invalidFileType",
          "Please upload a PDF or image file"
        )
      );
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(
        t("panel.document.fileTooLarge", "File must be smaller than 10MB")
      );
      return;
    }

    setUploadingType(pendingDocType);

    try {
      const result = await uploadToStorage(file, {
        directory: "strongbox",
        filename: `vehicle_${vehicleId}_${typeKey}_${Date.now()}`,
      });

      createDocument({
        name: typeKey,
        scan_id: result.url,
        vehicle_id: vehicleId,
        type: typeKey,
      } as any);
    } catch (err) {
      toast.error(
        t("panel.document.uploadFailed", "Failed to upload document")
      );
      setUploadingType(null);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setPendingDocType(null);
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        onChange={handleFileChange}
      />
      <div className={classes.container}>
        <div className={classes.headerRow}>
          <CustomTypography
            isUppercase
            text={t("panel.document.documents", "Documents")}
            variant={"h5"}
            styles={{ containerText: classes.contratTitle }}
          />
          <span className={classes.docCount}>
            {documents?.filter((d: any) => d.scan_id || d.scan).length ?? 0}/
            {dataDocuments.length}
          </span>
        </div>
        {dataDocuments.map((document, index) => {
          const docHasFile = hasDocument(document.id);
          const isUploading = uploadingType === document.id;

          return (
            <div
              className={`${classes.subContainer} ${
                docHasFile ? classes.docAvailable : classes.docMissing
              }`}
              key={index}
            >
              <div className={classes.subContainer2}>
                <IconWithBackground iconName={document.icoName} />
                <div className={classes.docInfo}>
                  <CustomTypography
                    text={t(document.titleKey, document.titleDefault)}
                    isUppercase
                    variant="link"
                    fontWeight={"bold"}
                  />
                  <span
                    className={
                      docHasFile ? classes.statusUploaded : classes.statusMissing
                    }
                  >
                    {docHasFile
                      ? t("panel.document.uploaded", "Uploaded")
                      : t("panel.document.missing", "Missing")}
                  </span>
                </div>
              </div>
              <div className={classes.actionButtons}>
                {isUploading ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    {docHasFile && (
                      <IconButton
                        aria-label={t("a11y.viewDocument", "View document")}
                        className={classes.iconButton}
                        onClick={() => openDocumentOrWarn(document.id)}
                        title={t("common.view", "View")}
                      >
                        <Icon
                          name="eye"
                          width={"20px"}
                          height={"20px"}
                          color={colors.BLACK}
                        />
                      </IconButton>
                    )}
                    <IconButton
                      aria-label={t("a11y.uploadDocument", "Upload document")}
                      className={classes.iconButton}
                      onClick={() => handleUploadClick(document.id)}
                      title={
                        docHasFile
                          ? t("panel.document.reupload", "Re-upload")
                          : t("panel.document.upload", "Upload")
                      }
                    >
                      <Icon
                        name={docHasFile ? "reload" : "plus"}
                        width={"20px"}
                        height={"20px"}
                        color={docHasFile ? colors.BLACK : colors.primary}
                      />
                    </IconButton>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};